import { NOTIFICATION_TYPES } from 'constants/notification-types';
import { useRouter } from 'next/router';
import { NotificationDto } from 'services/api/apis/notifications';
import { useNotificationsApi } from 'services/api/hooks/useNotificationsApi.hook';

export interface UseNotificationHandlersProps {
  onNotificationsMutate: () => void;
  onUnreadCountMutate: () => void;
  onClose?: () => void;
}

export const useNotificationHandlers = ({
  onNotificationsMutate,
  onUnreadCountMutate,
  onClose,
}: UseNotificationHandlersProps) => {
  const router = useRouter();
  const notificationsApi = useNotificationsApi();

  const handleNotificationClick = async (notification: NotificationDto) => {
    // Mark as read if unread
    if (!notification.isRead) {
      await notificationsApi.markAsRead(notification.id);
      onNotificationsMutate();
      onUnreadCountMutate();
    }

    // Handle feedback request notifications specially (opens feedback drawer)
    if (notification.type === NOTIFICATION_TYPES.EVENT_FEEDBACK_REQUEST) {
      // Extract eventId from redirectUrl or data
      const redirectUrl = notification.data?.redirectUrl;
      const eventId =
        notification.data?.eventId ||
        (typeof redirectUrl === 'string' && redirectUrl.includes('feedback=')
          ? redirectUrl.split('feedback=')[1]?.split('&')[0]
          : null);

      if (eventId) {
        // Close the notification drawer first
        onClose?.();

        // Add query param to URL to trigger global feedback drawer
        // Include fromNotification flag to track that we came from notification drawer
        void router.push(
          {
            pathname: router.pathname,
            query: { ...router.query, feedback: eventId, fromNotification: 'true' },
          },
          undefined,
          { shallow: true },
        );
      }
      return;
    }

    // Close the notification drawer
    onClose?.();

    // For all other notifications, navigate to redirectUrl if available
    const redirectUrl = notification.data?.redirectUrl;
    if (redirectUrl && typeof redirectUrl === 'string') {
      // Handle relative URLs
      if (redirectUrl.startsWith('/')) {
        void router.push(redirectUrl);
      } else if (redirectUrl.startsWith('http')) {
        // External URLs - open in new tab
        window.open(redirectUrl, '_blank');
      } else {
        // Handle query string only URLs (like ?feedback=eventId)
        const [pathname, queryString] = redirectUrl.split('?');
        const queryParams = queryString
          ? Object.fromEntries(new URLSearchParams(queryString))
          : {};

        void router.push({
          pathname: pathname || router.pathname,
          query: { ...router.query, ...queryParams },
        });
      }
    }
    // Note: Notifications without redirectUrl will still be marked as read and drawer will close
  };

  const handleMarkAllAsRead = async () => {
    await notificationsApi.markAllAsRead();
    onNotificationsMutate();
    onUnreadCountMutate();
  };

  return {
    handleNotificationClick,
    handleMarkAllAsRead,
  };
};

